import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

interface BlogGuideFiltersProps {
  title: string;
  children: React.ReactNode;
  /** Recompute layout when this changes (e.g. filter list / locale) */
  depsKey?: string;
}

const ROW_GAP = 16;
/** Extra room before switching to beside (avoids scrollbar-width oscillation) */
const ENTER_BESIDE_SLACK = 32;
/** Extra room before leaving beside */
const LEAVE_BESIDE_SLACK = 8;

const chevronBtnClass =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-[color,border-color,opacity] hover:border-blue-200 hover:text-blue-600 disabled:pointer-events-none disabled:opacity-30';

const BlogGuideFilters: React.FC<BlogGuideFiltersProps> = ({ title, children, depsKey = '' }) => {
  const { language } = useI18n();
  const rowRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const besideRef = useRef(false);
  const [beside, setBeside] = useState(false);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const prevLabel = language === 'fr' ? 'Filtres précédents' : 'Previous filters';
  const nextLabel = language === 'fr' ? 'Filtres suivants' : 'Next filters';

  const updateOverflow = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || besideRef.current) {
      setCanLeft(false);
      setCanRight(false);
      return;
    }
    const { scrollLeft, clientWidth, scrollWidth } = el;
    const nextLeft = scrollLeft > 2;
    const nextRight = scrollLeft + clientWidth < scrollWidth - 2;
    setCanLeft((prev) => (prev === nextLeft ? prev : nextLeft));
    setCanRight((prev) => (prev === nextRight ? prev : nextRight));
  }, []);

  const updateLayout = useCallback(() => {
    const row = rowRef.current;
    const titleEl = titleRef.current;
    const measure = measureRef.current;
    if (!row || !titleEl || !measure) return;

    const chipsWidth = measure.scrollWidth;
    const available = row.clientWidth;
    const needed = titleEl.offsetWidth + ROW_GAP + chipsWidth;
    const currentlyBeside = besideRef.current;
    const nextBeside = currentlyBeside
      ? needed <= available - LEAVE_BESIDE_SLACK
      : needed <= available - ENTER_BESIDE_SLACK;

    if (nextBeside !== currentlyBeside) {
      besideRef.current = nextBeside;
      setBeside(nextBeside);
    }

    // Compare chips to full row width (stable) — not scroller width after chevrons mount
    const nextNeedsScroll = !nextBeside && chipsWidth > available - 8;
    setNeedsScroll((prev) => (prev === nextNeedsScroll ? prev : nextNeedsScroll));

    requestAnimationFrame(updateOverflow);
  }, [updateOverflow]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    const run = () => updateLayout();
    run();
    const raf = requestAnimationFrame(run);

    window.addEventListener('resize', run);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(run) : null;
    ro?.observe(row);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', run);
      ro?.disconnect();
    };
  }, [updateLayout, depsKey]);

  useEffect(() => {
    if (beside) return;
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => updateOverflow();
    el.addEventListener('scroll', onScroll, { passive: true });
    updateOverflow();
    return () => el.removeEventListener('scroll', onScroll);
  }, [beside, needsScroll, updateOverflow, depsKey]);

  const scrollByAmount = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(140, el.clientWidth * 0.55), behavior: 'smooth' });
  };

  const scrollMaskStyle: React.CSSProperties | undefined =
    needsScroll && (canLeft || canRight)
      ? {
          WebkitMaskImage:
            canLeft && canRight
              ? 'linear-gradient(to right, transparent, #000 14px, #000 calc(100% - 14px), transparent)'
              : canLeft
                ? 'linear-gradient(to right, transparent, #000 14px, #000)'
                : 'linear-gradient(to right, #000, #000 calc(100% - 14px), transparent)',
          maskImage:
            canLeft && canRight
              ? 'linear-gradient(to right, transparent, #000 14px, #000 calc(100% - 14px), transparent)'
              : canLeft
                ? 'linear-gradient(to right, transparent, #000 14px, #000)'
                : 'linear-gradient(to right, #000, #000 calc(100% - 14px), transparent)',
        }
      : undefined;

  return (
    <div
      ref={rowRef}
      className={`relative mb-6 flex gap-3 sm:mb-8 sm:gap-4 ${
        beside ? 'flex-row items-center justify-between' : 'flex-col'
      }`}
    >
      <h2
        ref={titleRef}
        className="shrink-0 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl"
      >
        {title}
      </h2>

      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute left-0 top-0 -z-10 flex w-max gap-2"
        aria-hidden
      >
        {children}
      </div>

      {beside ? (
        <div className="flex shrink-0 gap-2">{children}</div>
      ) : (
        <div className="flex w-full min-w-0 items-center gap-1.5">
          {needsScroll && (
            <button
              type="button"
              onClick={() => scrollByAmount(-1)}
              disabled={!canLeft}
              aria-label={prevLabel}
              className={chevronBtnClass}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}

          <div
            ref={scrollerRef}
            style={scrollMaskStyle}
            className="flex min-w-0 flex-1 gap-2 overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {children}
          </div>

          {needsScroll && (
            <button
              type="button"
              onClick={() => scrollByAmount(1)}
              disabled={!canRight}
              aria-label={nextLabel}
              className={chevronBtnClass}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogGuideFilters;
